import { DrawerForm, DrawerSizeEnum, Switch, SwitchColorEnum } from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DetectionModal } from '@/components/DetectionModal';
import { InputArea } from '@/components/FormFields/InputArea';
import { InputFiles } from '@/components/FormFields/InputFiles';
import { InputLogos } from '@/components/FormFields/InputLogos';
import { InputSwitch } from '@/components/FormFields/InputSwitch';
import { InputText } from '@/components/FormFields/InputText';
import { RowContainer } from '@/components/FormFields/RowContainer';
import ReferedImages from '@/components/ReferedImages/ReferedImages';
import { MEGABYTES_TO_BYTES } from '@/constants';
import { DrawerFormDataType, FormTypeEnum, State } from '@/types';
import { IMAGE_PNG_JPG_ACCEPT } from '@/utils/files';

import { useFeaturedPeopleDrawerFormState } from '../../hooks/useFeaturedPeopleDrawerFormState';
import { TFeaturedPeopleType, TFeaturedPeopleForm } from '../../types';

export type TFeaturedPeopleDrawerFormProps = {
  isOpen: boolean;
  data?: DrawerFormDataType<TFeaturedPeopleType>;
  onClose: () => void;
  onSubmit: (data: TFeaturedPeopleForm) => void;
  isMutating: boolean;
};

export const FeaturedPeopleDrawerForm = ({
  data,
  isOpen,
  onClose,
  onSubmit,
  isMutating,
}: TFeaturedPeopleDrawerFormProps) => {
  const { t } = useTranslation();
  const { form, initialValues } = useFeaturedPeopleDrawerFormState();

  const handleSubmit = form.handleSubmit(data => {
    onSubmit(data);
  });

  const resetForm = () => {
    form.reset(initialValues);
  };

  useEffect(() => {
    if (!data?.data) return;

    const formData: TFeaturedPeopleForm = {
      ...data.data,
      description: data.data.description ?? '',
      surname: '',
      main_logo: data.data.parameters?.main_logo_presigned_url ?? '',
      logos: [],
      logos_presigned_urls: data.data.parameters?.logos_presigned_urls ?? [],
      alias: data.data.alias ?? '',
      state: data.data.state,
    };

    if (data.type === FormTypeEnum.UPDATE) {
      form.reset(formData);
    }

    if (data.type === FormTypeEnum.DUPLICATE) {
      form.reset({
        ...formData,
        name: `${data.data.name} (Copia)`,
      });
    }
  }, [data]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const { active_detection } = useWatch({
    control: form.control,
  });

  const isUpdating = data?.type === FormTypeEnum.UPDATE;
  const [openDetectionModal, setOpenDetectionModal] = useState(false);

  return (
    <>
      <DrawerForm
        title={
          !data?.data
            ? t('Crear nueva persona de interés')
            : data?.type === FormTypeEnum.UPDATE
              ? 'Editar persona de interés'
              : 'Duplicar persona de interés'
        }
        isOpen={isOpen}
        onCancel={() => {
          onClose();
          resetForm();
        }}
        isMutating={isMutating}
        onSubmit={handleSubmit}
        size={DrawerSizeEnum.MD}
        labels={{
          submit: t('Guardar'),
          cancel: t('Cancelar'),
        }}
      >
        <InputSwitch
          states={{ active: State.Active, inactive: State.Inactive }}
          form={form}
          name="state"
        />
        <RowContainer>
          <InputText
            form={form}
            name="name"
            required
            label={t('Nombre y apellidos')}
            placeholder={t('Escribe aquí el nombre y apellidos')}
          />
        </RowContainer>
        <InputArea
          form={form}
          name="alias"
          label={t('Alias')}
          placeholder={t('Escribe aquí los alias separados por comas')}
        />
        <InputArea
          form={form}
          name="description"
          label={t('Descripción')}
          placeholder={t('Escribe aquí tu descripción')}
        />

        <div className="flex items-end gap-4">
          <InputFiles
            form={form}
            name="main_logo"
            label={t('Imagen principal')}
            required={active_detection}
            isImage
            singleFile
            accept={IMAGE_PNG_JPG_ACCEPT}
            placeholder="Sube una imagen"
          />

          <Switch
            className="flex w-full"
            endLabel={t('Detección por imagen')}
            checked={active_detection}
            color={SwitchColorEnum.SUCCESS}
            onChange={active => {
              if (active) {
                return form.setValue('active_detection', true);
              }

              if (isUpdating) {
                return setOpenDetectionModal(true);
              }

              form.setValue('active_detection', false);
            }}
          />
        </div>

        <div
          className={clsx('w-full', {
            'pointer-events-none opacity-50': !active_detection,
          })}
        >
          <ReferedImages />
          <InputLogos
            form={form}
            name="logos"
            label={t('Seleccionar archivos')}
            newFiles
            format={t('imagen')}
            accept={IMAGE_PNG_JPG_ACCEPT}
            maxSize={25 * MEGABYTES_TO_BYTES}
          />
          <InputLogos
            form={form}
            name="logos_presigned_urls"
            label={t('Imágenes subidas')}
            format={t('imagen')}
            maxSize={25 * MEGABYTES_TO_BYTES}
          />
        </div>
      </DrawerForm>

      <DetectionModal
        onClose={() => setOpenDetectionModal(false)}
        form={form}
        open={openDetectionModal}
      />
    </>
  );
};
